import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { type TimespanModel } from 'Pages/Reports/PrioritizationReport.types'
import { Box, IconButton, Menu, MenuItem, CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'
import { ProgramChoiceReportTableView, type ProgramChoiceReportModel } from './ProgramChoiceReportTableView'
import { getShortYear } from 'Pages/Reports/GSRPCapacityReport.utils'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { ReportHeader } from 'Pages/Reports/ReportHeader'

export function ProgramChoiceReport (): JSX.Element {
  const [timespans, setTimespans] = useState<TimespanModel[]>([])
  const [selectedTimespanID, setSelectedTimespanID] = useState<number | null>(null)
  const [timespanMenuAnchor, setTimespanMenuAnchor] = useState<null | HTMLElement>(null)
  const [loading, setLoading] = useState(false)
  const [careManagementOnly, setCareManagementOnly] = useState(false)
  const [singleOptionOnly, setSingleOptionOnly] = useState(false)
  const [reportData, setReportData] = useState<ProgramChoiceReportModel[]>([])

  useEffect(() => {
    const fetchTimespans = async (): Promise<void> => {
      const { response, success } = await sendGet('/Report/PrioritizationReport/GetTimespans', {})
      if (success && Array.isArray(response)) {
        setTimespans(response)

        // Default to timespan that begins in current year
        const currentYear = new Date().getFullYear()
        const defaultTimespan = response.find((ts: TimespanModel) => {
          if (ts.startDate != null) {
            const startYear = new Date(ts.startDate).getFullYear()
            return startYear === currentYear
          }
          return false
        })

        if (defaultTimespan != null) {
          setSelectedTimespanID(defaultTimespan.id)
        } else if (response.length > 0) {
          // Fallback to first timespan if no match
          setSelectedTimespanID(response[0].id)
        }
      }
    }
    void fetchTimespans()
  }, [])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (selectedTimespanID != null) {
        setLoading(true)
        const { response, success } = await sendGet(`/Report/ProgramChoiceReport/GetProgramChoiceReport?timespanID=${selectedTimespanID}&careManagementOnly=${(careManagementOnly ? 'Y' : 'N')}&singleOptionOnly=${(singleOptionOnly ? 'Y' : 'N')}`, {})
        if (success) {
          setReportData(response)
        } else {
          toast.error('Failed to generate report.')
        }
        setLoading(false)
      }
    }
    void fetchData()
  }, [selectedTimespanID, careManagementOnly, singleOptionOnly])

  return <>
    <ReportHeader title="Program Choice Report" />

    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Box sx={{ pr: 2 }} >
          <CheckBoxWithLabel
            label="Only show children with an active Care Management"
            edge='start'
            dataTestId="careManagementOnly"
            name='careManagementOnly'
            setValue={(value) => { setCareManagementOnly(value) }}
            value={careManagementOnly}
          />
        </Box>
        <Box sx={{ pr: 2 }} >
          <CheckBoxWithLabel
            label="Only show children with a single choice"
            edge='start'
            dataTestId="singleOptionOnly"
            name='singleOptionOnly'
            setValue={(value) => { setSingleOptionOnly(value) }}
            value={singleOptionOnly}
          />
        </Box>
        <Box >
          {/* Year selector dropdown */}
          <IconButton
            onClick={(e) => { setTimespanMenuAnchor(e.currentTarget) }}
            aria-label="Select School Year"
            sx={{
              border: '1px solid #E1E1E1',
              borderRadius: 1,
              width: 48,
              height: 48,
              bgcolor: 'white',
              '&:hover': {
                bgcolor: '#F5F5F5'
              }
            }}
          >
            <Box sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1.2, textAlign: 'center' }}>
              {selectedTimespanID != null ? getShortYear(timespans.find(ts => ts.id === selectedTimespanID)?.name ?? '') : '26/27'}
            </Box>
          </IconButton>
          <Menu
            anchorEl={timespanMenuAnchor}
            open={Boolean(timespanMenuAnchor)}
            onClose={() => { setTimespanMenuAnchor(null) }}
          >
            {timespans.map((ts) => (
              <MenuItem
                key={ts.id}
                onClick={() => {
                  setSelectedTimespanID(ts.id)
                  setTimespanMenuAnchor(null)
                }}
                selected={ts.id === selectedTimespanID}
              >
                {ts.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
    </Box >

    {loading && (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
    }

    {!loading && <ProgramChoiceReportTableView reportData={reportData} />}
  </>
}
